import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Link } from 'react-router-dom';
import { FontAwesome } from 'components/ui';
import { Checkbox } from 'components/inputs';
import { Table, Input, Alert } from 'reactstrap';

import { MarkQueue, MarkDebouncer } from './marking';

import './styles/Grid.scss';

// Unregistered soldiers are listed so the teacher sees the whole class. The 12 Pesukim
// campaign is the one thing they are enrolled in before they register, so those columns
// stay live for them and the rest are greyed out. Only an explicit false counts - a
// soldier from a payload without the flag is markable everywhere.
// Absentees are also listed so the class still reads as complete, but every column
// is locked for them - they are not in today, so nothing should be marked.
const isRegistered = soldier => !soldier || soldier.registered !== false;
const isAbsent = soldier => !!( soldier && soldier.absent );
const canMark = ( soldier, mission ) =>
  !isAbsent( soldier ) && ( isRegistered( soldier ) || !!( mission && mission.pesukim ) );

class GridCell extends Component {

  state = { waiting: false, typed: null }

  // while a value is held, show what was typed - the store has not got it yet
  debouncer = new MarkDebouncer( ( key, waiting ) => this.setState(
    waiting ? { waiting: true } : { waiting: false, typed: null }
  ));

  componentWillUnmount() {
    // do not silently drop a number typed but not yet sent - SAVE_DELAY is
    // long enough that a date change or tab switch can easily land inside it
    this.debouncer.stop();
  }

  send = value => {
    const { mission, soldier } = this.props;
    this.props.onChange( value, mission, soldier );
  }

  onChange = e => {
    if ( e.target.type === 'number' ) {
      // hold the mark back while the number is still being typed
      const value = e.target.value;
      this.setState({ typed: value });
      this.debouncer.hold( 'cell', value, this.send );
    } else {
      this.send( e.target.checked ? '1' : '0' );
    }
  }

  // leaving the box, or pressing enter, means the number is finished
  onBlur = () => this.debouncer.flush( 'cell' );

  onKeyDown = e => {
    if ( e.key === 'Enter' ) this.debouncer.flush( 'cell' );
  }

  renderInput() {
    let { mission, soldier, value } = this.props;
    // if it is a quantity based mission
    if ( mission.quantity ) {
      // set the value of the input
      if ( !value && soldier && !!soldier.marks[ mission.grid_id ] )
        value = soldier.marks[ mission.grid_id ].done_qty;
      // hide error
      if ( !value && soldier )
        value = value || '';
      // the box is bound to the store, which will not hold a held value yet
      if ( this.state.typed !== null )
        value = this.state.typed;
      // return a input component
      return <Input 
        min='0'
        type='number'
        value={ value } 
        disabled={ this.props.disabled }
        onChange={ this.onChange }
        onBlur={ this.onBlur }
        onKeyDown={ this.onKeyDown }
        />;
    }

    if ( !value && soldier )
      value = !!soldier.marks[ mission.grid_id ];
    // return a checkbox for on/off based tasks
    // ( locked while its mark is saving so the same box cannot be sent twice )
    return <Checkbox 
      checked={ value }
      disabled={ !!mission.disable || this.props.pending || this.props.disabled }
      onChange={ this.onChange } />
  }

  render() {
    const { pending } = this.props;
    const { waiting } = this.state;
    // the wrapper is always rendered so a pending mark never remounts the
    // input underneath it - that would drop focus mid-typing on quantities
    return (
      <span className={ classnames( 'mark-cell', { pending, waiting } ) }>
        { this.renderInput() }
        { waiting && !pending && // typed, not sent yet - sits beside the box
          <span className='mark-cell-waiting' title='Waiting for you to finish typing'>
            <FontAwesome icon='pencil-alt' />
          </span>
        }
        { pending &&
          <span className='mark-cell-spinner'>
            <FontAwesome icon='circle-notch' spin />
          </span>
        }
      </span>
    );
  }
}

export class Grid extends Component {

  static propTypes = {
    date: PropTypes.number,
    type: PropTypes.string,
    soldiers: PropTypes.array,
    missions: PropTypes.array,
  }

  state = { pending: {} }

  queue = new MarkQueue( ( key, saving ) => this.setState( ({ pending }) => {
    const next = { ...pending };

    if ( saving ) next[ key ] = true;
    else delete next[ key ];

    return { pending: next };
  }));

  componentWillUnmount() {
    this.queue.stop();
  }

  checkAll = ( value, mission ) => {
    // mark the task
    return this.queue.run( `all:${ mission.grid_id }`, () => {
      const user_ids = this.props.soldiers
        .filter( soldier => canMark( soldier, mission ) ) // skip anyone this column is greyed out for
        .filter( soldier => // filter to soldiers that either
          !soldier.marks[ mission.grid_id ] || // 1. do not have a mark for this grid_id
          soldier.marks[ mission.grid_id ].done_qty !== value // 2. do have a mark that does not match the one entered at the top
        ).map( soldier => soldier.user_id );

      return this.props.markTask(
        this.props.type, user_ids, mission.grid_id, 
        mission.mark_date || this.props.date, value
      );
    });
  }

  toggleTask = ( value, mission, soldier ) => {
    const user_ids = [ soldier.user_id ];
    return this.queue.run( `${ soldier.user_id }:${ mission.grid_id }`, () =>
      this.props.markTask(
        this.props.type, user_ids,  mission.grid_id, 
        mission.mark_date || this.props.date, value
      )
    );
  }

  render(){
    let { soldiers, missions } = this.props;
    const { pending } = this.state;
    // hide disabled missions from the GUI
    // mandatory missions used to be forced visible; teachers may now hide them too
    // missions = missions.filter( mission => !mission.disabled || mission.mandatory_qty >= 1 );
    missions = missions.filter( mission => !mission.disabled );

    if ( missions.length === 0 )
      return <Alert color="danger">No Available Tasks</Alert>
    
    return (
      <Table className='Grid' bordered striped hover responsive size="sm">
        <thead>
          <tr className='Grid-row header'>
            <th>
              <span>Missions <FontAwesome icon='arrow-right' /></span>
              <hr/>
              <span>Soldiers <FontAwesome icon='arrow-down' /></span>
            </th>
            { missions.map( ( mission, index ) =>
              <th key={ index }>
                <span>{ mission.cat } </span>
                { mission.mandatory_qty >= 1 && <FontAwesome icon='star' /> }
              </th>
            )}
          </tr>
        </thead>
        
        <tbody>
          <tr className='Grid-row'>
              <td>
                <strong>All Soldiers</strong>
              </td>
              { missions.map( ( mission, index ) =>
                <td key={ index } >
                  <GridCell 
                    mission={ mission }
                    onChange={ this.checkAll }
                    pending={ !!pending[ `all:${ mission.grid_id }` ] } />
                </td>
              )}
            </tr>

          { soldiers.map( ( soldier, index ) =>
            <tr className={ classnames( 'Grid-row', {
              unregistered: !isRegistered( soldier ),
              absent: isAbsent( soldier )
            } ) } key={ index }>
              <td>
                <small>
                  { isAbsent( soldier )
                    ? 'Absent'
                    : isRegistered( soldier )
                      ? soldier.rank && soldier.rank.name
                      : 'Not Registered' }
                </small><br/>
                <Link to={`/bm/soldiers/${soldier.user_id}`}>{ soldier.name }</Link>
              </td>
              { missions.map( ( mission, index ) =>
                <td key={ index } >
                  <GridCell 
                    soldier={ soldier }
                    mission={ mission }
                    disabled={ !canMark( soldier, mission ) }
                    onChange={ this.toggleTask }
                    pending={ !!pending[ `${ soldier.user_id }:${ mission.grid_id }` ] } />
                </td>
              )}
            </tr>
          )}
        </tbody>
      </Table>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/missions/Grid.jsx